/**
 * One attendance record as a card — the narrow-screen half of both archives.
 *
 * The counterpart to `AttendanceHistoryTable`. A table below about 1024px is a horizontal
 * scroll, and a horizontal scroll is where a column goes to be missed: the admin checking a
 * handover on their phone would have had to swipe to reach the one column they opened the
 * page for.
 *
 * So the same record, stacked. The name leads because it is what is being looked up; the
 * status keeps its chip and stays on the right where the eye already expects it from the
 * roster; everything else drops to the small type underneath. `AttendanceRecordFacts`
 * carries what the table hides behind its chevron — who checked the child in, who collected
 * them, the absence reason — which on a phone is better read than expanded, because a card
 * has the width for it and a tap is a tap.
 */

import { AttendanceRecordFacts } from './AttendanceRecordFacts';
import { AttendanceStatusBadge } from './AttendanceStatusBadge';
import { HISTORY_STATUS_LABEL, type AttendanceHistoryRow } from '../../utils/attendanceHistory';
import { formatDateDisplay } from '../../utils/date';

interface Props {
  row: AttendanceHistoryRow;
  /**
   * The line under the name — which class, and whatever else does not repeat down the
   * list. Null suppresses it: inside Chinese G3 the class is the heading and the grade is
   * the class, so the line would say the same thing on every card.
   */
  context?: string | null;
  /**
   * Whether the card carries its own date.
   *
   * False where the cards are already grouped under one — the teacher's day cards — and
   * true where a list runs across dates, as the admin's does.
   */
  showDate?: boolean;
}

/**
 * A list item, not a standalone box: both callers render these into a `<ul>` with dividers,
 * which reads as one list rather than a stack of floating panels.
 */
export function AttendanceRecordCard({ row, context = null, showDate = false }: Props) {
  return (
    <li className="flex items-start gap-3 px-3 py-2.5">
      <div className="min-w-0 flex-1">
        {showDate && (
          <p className="text-[11px] text-gray-400">{formatDateDisplay(row.sessionDate)}</p>
        )}
        <p className="font-medium text-gray-900">{row.studentName}</p>
        {context && <p className="mt-0.5 text-[11px] text-gray-500">{context}</p>}
        <AttendanceRecordFacts record={row} />
      </div>
      <AttendanceStatusBadge status={row.status} labels={HISTORY_STATUS_LABEL} />
    </li>
  );
}
